import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { PageHeader, fmt, LangFlags, money } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Film, Play, Trash2, Loader2, Globe, Clapperboard, Eye, Heart, TrendingUp, Save, Calendar as CalendarIcon, DollarSign } from "lucide-react";
import { toast } from "sonner";

const STATUS = [
  { v: "draft", l: "Draft", c: "#94A3B8", cls: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
  { v: "approved", l: "Approved", c: "#06B6D4", cls: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30" },
  { v: "published", l: "Published", c: "#10B981", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
];
const statusMeta = (s) => STATUS.find((x) => x.v === (s || "draft")) || STATUS[0];

const ratio = (v) => (v?.views ? Math.round((v.likes / v.views) * 10000) / 100 : 0);

const VPLATFORMS = [
  { key: "youtube", label: "YouTube", color: "#FF0000" },
  { key: "tiktok", label: "TikTok", color: "#FF0050" },
  { key: "instagram", label: "Instagram", color: "#E1306C" },
];
const ratioOf = (views, likes) => (views ? Math.round((likes / views) * 10000) / 100 : 0);
const emptyPM = () => ({ youtube: { views: 0, likes: 0, spend: 0 }, tiktok: { views: 0, likes: 0, spend: 0 }, instagram: { views: 0, likes: 0, spend: 0 } });

function Thumb({ video, className }) {
  const [err, setErr] = useState(false);
  if (!video.thumbnail_path || err) {
    return <div className={`${className} flex items-center justify-center`}><Clapperboard className="w-10 h-10 text-primary/40" /></div>;
  }
  return <img src={`${API}/videos/${video.id}/thumbnail`} alt={video.title} onError={() => setErr(true)} className={`${className} object-cover`} />;
}

export default function VideoLibrary() {
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [pm, setPm] = useState(emptyPM());
  const [pubDate, setPubDate] = useState("");
  const [filters, setFilters] = useState({ language: "all", video_type: "all", category_id: "all" });
  const [sort, setSort] = useState("recent");
  const [langs, setLangs] = useState([]);
  const [vtypes, setVtypes] = useState([]);
  const [versionLang, setVersionLang] = useState("");
  const [versionUploading, setVersionUploading] = useState(false);
  const versionRef = useRef();
  const fileRef = useRef();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form, setForm] = useState({ title: "", language: "English", video_type: "Short", category_id: "none", release_date: "", description: "", file: null });

  const load = () => {
    api.get("/videos").then((r) => setVideos(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/settings/languages").then((r) => setLangs(r.data));
    api.get("/settings/video-types").then((r) => setVtypes(r.data));
  };
  useEffect(() => { load(); }, []);

  const openDetail = (v) => {
    setDetail(v);
    const base = emptyPM();
    const src = v.platform_metrics || {};
    for (const k of Object.keys(base)) {
      base[k] = { views: (src[k]?.views) || 0, likes: (src[k]?.likes) || 0, spend: (src[k]?.spend) || 0 };
    }
    setPm(base);
    setPubDate(v.published_date || "");
    setVersionLang("");
  };

  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && videos.length) {
      const v = videos.find((x) => x.id === openId);
      if (v) openDetail(v);
    }
  }, [videos, searchParams]);

  const closeDetail = () => {
    setDetail(null);
    if (searchParams.get("open")) { searchParams.delete("open"); setSearchParams(searchParams, { replace: true }); }
  };

  const upload = async () => {
    if (!form.file) return toast.error("Choose a video file");
    if (!form.title.trim()) return toast.error("Title required");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", form.file);
    fd.append("title", form.title);
    fd.append("language", form.language);
    fd.append("video_type", form.video_type);
    fd.append("category_id", form.category_id === "none" ? "" : form.category_id);
    fd.append("release_date", form.release_date);
    fd.append("description", form.description);
    try {
      await api.post("/videos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video uploaded");
      setOpen(false);
      setForm({ title: "", language: "English", video_type: "Short", category_id: "none", release_date: "", description: "", file: null });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const saveMetrics = async () => {
    const payload = {};
    for (const p of VPLATFORMS) {
      payload[p.key] = { views: Number(pm[p.key]?.views) || 0, likes: Number(pm[p.key]?.likes) || 0, spend: Number(pm[p.key]?.spend) || 0 };
    }
    const { data } = await api.patch(`/videos/${detail.id}/metrics`, { platform_metrics: payload });
    toast.success("Performance updated");
    setDetail(data);
    load();
  };

  const setPmField = (key, field, value) => setPm((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));

  const savePublishedDate = async () => {
    const { data } = await api.put(`/videos/${detail.id}`, { published_date: pubDate });
    toast.success("Published date updated");
    setDetail(data);
    load();
  };

  const changeStatus = async (status) => {
    const { data } = await api.put(`/videos/${detail.id}`, { status });
    toast.success(status === "published" ? "Published — now live in Marketing" : `Marked as ${status}`);
    setDetail(data);
    setPubDate(data.published_date || "");
    load();
  };

  const addVersion = async (file) => {
    if (!file) return;
    if (!versionLang) return toast.error("Pick the language for this version first");
    setVersionUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("language", versionLang);
    try {
      await api.post(`/videos/${detail.id}/versions`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${versionLang} version added`);
      if (versionRef.current) versionRef.current.value = "";
      setVersionLang("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      if (versionRef.current) versionRef.current.value = "";
      setVersionUploading(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/videos/${id}`);
    toast.success("Video removed");
    setDetail(null);
    load();
  };

  const catOf = (id) => categories.find((c) => c.id === id);
  const versions = detail ? videos.filter((v) => v.dubbed_from === detail.id) : [];

  let filtered = videos.filter((v) =>
    (filters.language === "all" || v.language === filters.language) &&
    (filters.video_type === "all" || v.video_type === filters.video_type) &&
    (filters.category_id === "all" || v.category_id === filters.category_id)
  );
  if (sort === "views") filtered = [...filtered].sort((a, b) => (b.views || 0) - (a.views || 0));
  else if (sort === "ratio") filtered = [...filtered].sort((a, b) => ratio(b) - ratio(a));

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="video-library-container">
      <PageHeader title="Video Library" subtitle="Every produced video, tagged, measured and searchable.">
        <Button onClick={() => setOpen(true)} data-testid="upload-video-button" className="font-semibold">
          <Upload className="w-4 h-4 mr-1.5" /> Upload Video
        </Button>
      </PageHeader>

      <div className="flex flex-wrap gap-3 mb-6">
        <Select value={filters.language} onValueChange={(v) => setFilters({ ...filters, language: v })}>
          <SelectTrigger className="w-40" data-testid="video-language-filter"><SelectValue placeholder="Language" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Languages</SelectItem>{langs.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.video_type} onValueChange={(v) => setFilters({ ...filters, video_type: v })}>
          <SelectTrigger className="w-36" data-testid="video-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Types</SelectItem>{vtypes.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.category_id} onValueChange={(v) => setFilters({ ...filters, category_id: v })}>
          <SelectTrigger className="w-44" data-testid="video-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Categories</SelectItem>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-44" data-testid="video-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Most Recent</SelectItem>
            <SelectItem value="views">Top Views</SelectItem>
            <SelectItem value="ratio">Best Likes/View</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-border rounded-2xl">
          <Film className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No videos yet. Upload your first produced video.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((v) => {
            const cat = catOf(v.category_id);
            return (
              <div key={v.id} onClick={() => openDetail(v)} data-testid="video-card-item"
                className="group glass border border-border rounded-2xl overflow-hidden cursor-pointer hover:border-primary/40 transition-all duration-300 hover:-translate-y-1">
                <div className="aspect-video bg-gradient-to-br from-secondary to-background relative">
                  <Thumb video={v} className="absolute inset-0 w-full h-full" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center"><Play className="w-5 h-5 text-primary-foreground ml-0.5" fill="currentColor" /></div>
                  </div>
                  <Badge className="absolute top-2 left-2 bg-black/60 text-white border-0 capitalize">{v.video_type}</Badge>
                  <span className="absolute top-2 left-2 mt-7 flex items-center gap-1 bg-black/60 rounded-md px-1.5 py-1" data-testid="video-card-flags">
                    <LangFlags language={v.language} />
                  </span>
                  <Badge className={`absolute top-2 right-2 border capitalize text-[10px] ${statusMeta(v.status).cls}`} data-testid="video-card-status">{statusMeta(v.status).l}</Badge>
                  <div className="absolute bottom-2 right-2 flex gap-1.5">
                    <Badge className="bg-black/70 text-white border-0 text-[10px]" data-testid="video-card-views"><Eye className="w-2.5 h-2.5 mr-1" />{fmt(v.views || 0)}</Badge>
                    <Badge className="bg-black/70 text-primary border-0 text-[10px]" data-testid="video-card-ratio"><Heart className="w-2.5 h-2.5 mr-1" fill="currentColor" />{ratio(v)}%</Badge>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-sm truncate">{v.title}</h3>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <Badge variant="secondary" className="text-[10px]" data-testid="video-tag-badge"><LangFlags language={v.language} className="mr-1" />{v.language}</Badge>
                    {cat && <Badge variant="outline" className="text-[10px]" style={{ borderColor: cat.color, color: cat.color }}>{cat.name}</Badge>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-border">
          <DialogHeader><DialogTitle className="font-display">Upload Video</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">A poster thumbnail is generated automatically from your video.</p>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Video file</Label>
              <Input ref={fileRef} type="file" accept="video/*" onChange={(e) => setForm({ ...form, file: e.target.files[0] })} data-testid="video-file-input" />
            </div>
            <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="video-title-input" placeholder="Episode 12 — Final Cut" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Language</Label>
                <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                  <SelectTrigger data-testid="video-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{langs.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Type</Label>
                <Select value={form.video_type} onValueChange={(v) => setForm({ ...form, video_type: v })}>
                  <SelectTrigger data-testid="video-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{vtypes.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Category</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger data-testid="video-category-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Release date</Label><Input type="date" value={form.release_date} onChange={(e) => setForm({ ...form, release_date: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button onClick={upload} disabled={uploading} data-testid="video-upload-submit" className="font-semibold">
              {uploading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Uploading...</> : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && closeDetail()}>
        <DialogContent className="glass border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="video-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-display pr-8">{detail.title}</DialogTitle></DialogHeader>
              <div className="relative z-0 w-full h-56 sm:h-64 rounded-xl overflow-hidden bg-black shrink-0">
                <video controls className="w-full h-full" src={`${API}/videos/${detail.id}/stream`} poster={detail.thumbnail_path ? `${API}/videos/${detail.id}/thumbnail` : undefined} data-testid="video-player" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="capitalize">{detail.video_type}</Badge>
                <Badge variant="secondary" className="flex items-center gap-1"><LangFlags language={detail.language} size="lg" />{detail.language}</Badge>
                {catOf(detail.category_id) && <Badge variant="outline" style={{ borderColor: catOf(detail.category_id).color, color: catOf(detail.category_id).color }}>{catOf(detail.category_id).name}</Badge>}
                {detail.release_date && <Badge variant="secondary">Release {new Date(detail.release_date).toLocaleDateString()}</Badge>}
              </div>
              {detail.description && <p className="text-sm text-muted-foreground">{detail.description}</p>}

              {/* Publishing status */}
              <div className="relative z-10 bg-secondary/30 rounded-xl p-4" data-testid="video-status-panel">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <h4 className="font-display font-semibold text-sm">Publishing Status</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">Published videos appear live in the Marketing section.</p>
                  </div>
                  <div className="flex gap-1 bg-background/60 p-1 rounded-lg">
                    {STATUS.map((s) => (
                      <button key={s.v} onClick={() => changeStatus(s.v)} data-testid={`video-status-${s.v}`}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${(detail.status || "draft") === s.v ? "text-background" : "text-muted-foreground hover:text-foreground"}`}
                        style={(detail.status || "draft") === s.v ? { background: s.c } : {}}>
                        {s.l}
                      </button>
                    ))}
                  </div>
                </div>
                {detail.status === "published" && (
                  <div className="mt-4 pt-4 border-t border-border/60 flex items-end gap-2 flex-wrap" data-testid="published-date-row">
                    <div className="space-y-1.5">
                      <Label className="flex items-center gap-1.5 text-xs"><CalendarIcon className="w-3.5 h-3.5" /> Published on</Label>
                      <Input type="date" value={pubDate ? pubDate.slice(0, 10) : ""} onChange={(e) => setPubDate(e.target.value)} data-testid="published-date-input" className="w-44" />
                    </div>
                    <Button size="sm" variant="secondary" onClick={savePublishedDate} data-testid="published-date-save">Save date</Button>
                    <p className="text-[11px] text-muted-foreground ml-auto max-w-[45%]">Defaults to the day you publish. Shown on the Calendar as a Published marker.</p>
                  </div>
                )}
              </div>

              {/* Per-video analytics */}
              <div className="bg-secondary/30 rounded-xl p-4 space-y-4" data-testid="video-metrics-panel">
                {(() => {
                  const oViews = VPLATFORMS.reduce((s, p) => s + (Number(pm[p.key]?.views) || 0), 0);
                  const oLikes = VPLATFORMS.reduce((s, p) => s + (Number(pm[p.key]?.likes) || 0), 0);
                  const oSpend = VPLATFORMS.reduce((s, p) => s + (Number(pm[p.key]?.spend) || 0), 0);
                  return (
                    <>
                      <div className="flex items-center justify-between">
                        <h4 className="font-display font-semibold text-sm flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-primary" /> Performance</h4>
                        <Badge className="bg-primary/15 text-primary border-primary/30" data-testid="video-detail-ratio">
                          <Heart className="w-3 h-3 mr-1" fill="currentColor" /> {ratioOf(oViews, oLikes)}% overall L/V
                        </Badge>
                      </div>

                      <div className="grid grid-cols-3 gap-3" data-testid="video-overall-summary">
                        <div className="bg-background/50 rounded-lg p-3 text-center"><span className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-0.5">Views</span><span className="font-display text-lg font-bold" data-testid="overall-views">{fmt(oViews)}</span></div>
                        <div className="bg-background/50 rounded-lg p-3 text-center"><span className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-0.5">Likes</span><span className="font-display text-lg font-bold" data-testid="overall-likes">{fmt(oLikes)}</span></div>
                        <div className="bg-background/50 rounded-lg p-3 text-center"><span className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-0.5">Likes/View</span><span className="font-display text-lg font-bold text-primary">{ratioOf(oViews, oLikes)}%</span></div>
                      </div>

                      <p className="text-xs text-muted-foreground -mb-1">Enter the latest views &amp; likes and promotion spend for each platform. Overall is the sum across platforms.</p>
                      <div className="flex items-center justify-between bg-background/50 rounded-lg px-3 py-2" data-testid="video-total-spend">
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><DollarSign className="w-3.5 h-3.5 text-emerald-400" /> Total promotion spend</span>
                        <span className="font-display font-bold text-emerald-400">{money(oSpend)}</span>
                      </div>
                      <div className="space-y-3">
                        {VPLATFORMS.map((p) => {
                          const v = Number(pm[p.key]?.views) || 0, l = Number(pm[p.key]?.likes) || 0;
                          return (
                            <div key={p.key} className="bg-background/40 rounded-lg p-3" data-testid={`platform-metrics-${p.key}`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="flex items-center gap-1.5 text-xs font-semibold"><span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />{p.label}</span>
                                <span className="text-[11px] text-muted-foreground" data-testid={`${p.key}-ratio`}>{ratioOf(v, l)}% L/V</span>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <Label className="flex items-center gap-1 text-[11px]"><Eye className="w-3 h-3" /> Views</Label>
                                  <Input type="number" value={pm[p.key]?.views} onChange={(e) => setPmField(p.key, "views", e.target.value)} data-testid={`${p.key}-views-input`} />
                                </div>
                                <div className="space-y-1">
                                  <Label className="flex items-center gap-1 text-[11px]"><Heart className="w-3 h-3" /> Likes</Label>
                                  <Input type="number" value={pm[p.key]?.likes} onChange={(e) => setPmField(p.key, "likes", e.target.value)} data-testid={`${p.key}-likes-input`} />
                                </div>
                              </div>
                              <div className="space-y-1 mt-3">
                                <Label className="flex items-center gap-1 text-[11px]"><DollarSign className="w-3 h-3" /> Promotion spend ($)</Label>
                                <Input type="number" step="0.01" value={pm[p.key]?.spend} onChange={(e) => setPmField(p.key, "spend", e.target.value)} data-testid={`${p.key}-spend-input`} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <Button size="sm" onClick={saveMetrics} data-testid="video-metrics-save" className="w-full font-semibold"><Save className="w-3.5 h-3.5 mr-1.5" /> Save Performance</Button>
                    </>
                  );
                })()}
              </div>

              {/* Language versions */}
              <div className="bg-secondary/30 rounded-xl p-4 space-y-3" data-testid="video-versions-panel">
                <h4 className="font-display font-semibold text-sm flex items-center gap-1.5"><Globe className="w-4 h-4 text-primary" /> Language Versions</h4>
                <p className="text-xs text-muted-foreground -mt-1">Dub this on ElevenLabs' website, then upload the finished file here as a language version linked to this original.</p>
                {versions.length > 0 && (
                  <div className="space-y-1.5">
                    {versions.map((vv) => (
                      <button key={vv.id} onClick={() => openDetail(vv)} data-testid="video-version-item"
                        className="w-full flex items-center gap-2 text-xs bg-background/50 rounded-lg px-3 py-2 hover:bg-background/80 transition-colors">
                        <LangFlags language={vv.language} className="shrink-0" />
                        <span className="font-medium">{vv.language}</span>
                        <span className="text-muted-foreground ml-auto truncate max-w-[55%]">{vv.title}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Select value={versionLang} onValueChange={setVersionLang}>
                    <SelectTrigger data-testid="version-language-select"><SelectValue placeholder="Language of the dub" /></SelectTrigger>
                    <SelectContent className="max-h-64">{langs.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <input ref={versionRef} type="file" accept="video/*" onChange={(e) => addVersion(e.target.files[0])} className="hidden" data-testid="version-file-input" />
                  <Button onClick={() => { if (!versionLang) return toast.error("Pick the language first"); versionRef.current?.click(); }} disabled={versionUploading} data-testid="version-upload-button" className="font-semibold shrink-0">
                    {versionUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1.5" /> Upload dub</>}
                  </Button>
                </div>
              </div>

              <p className="text-xs text-muted-foreground font-mono">Uploaded by {detail.uploaded_by} · {fmt(detail.size)} bytes</p>
              <DialogFooter>
                <Button variant="ghost" onClick={() => remove(detail.id)} data-testid="video-delete-button" className="text-destructive hover:text-destructive">
                  <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
